import { snakeCase } from 'lodash/fp';

import {
  CombinedStepType,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  findComponentConfigPath,
  isScreenStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import {
  COMPONENT_JSONLOGIC_KEYS,
  ScreenEditorComponentConfigTypes,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import { SERVER_EVALUATED_SUFFIX } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import {
  ComponentJsonLogicKey,
  ComponentJsonLogicRule,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  formatJSONLogicRule,
  getConfigOnComponentWithJSONLogic,
  isConfigJSONLogic,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import { SelectedComponentTab } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { DISPLAY_FOCUS_SEARCH_PARAM } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { uiStepAllComponentConfigs } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  getAllWorkflowActionSteps,
  isFieldsReferenceActionStep,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import {
  EXTRACTION_FIELD_TYPE_ADDRESS_FIELDS,
  EXTRACTION_FIELD_TYPE_NAME_FULL_FIELDS,
  ExtractionFieldType,
  ExtractionType,
} from 'dashboard/models/document';
import {
  CombinedStepConfig,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { BaseTrigger } from 'dashboard/models/trigger';
import {
  WorkflowStep,
  WorkflowStepActionKey,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { FieldSchema } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import {
  isUiFormComponentConfig,
  isUiFormFieldOptionComponentConfig,
  UiFormFieldOptionComponentConfig,
} from 'lib/interfaces/ui-components';

const INQUIRY_FIELD_PREFIX = `${BaseTrigger}.fields.`;

// map of combined step types to the field key attributes within their respective configs
export const CombinedStepFieldKeyMap: Record<CombinedStepType, string[]> = {
  [UiStepType.Document]: ['field-key-document'],
  [UiStepType.GovernmentId]: ['field-key-country', 'field-key-idclass', 'field-key-document'],
  [UiStepType.Selfie]: ['field-key-selfie'],
  [UiStepType.SelfieReflection]: ['field-key-selfie'],
};

export type FieldReferenceConfig = {
  /** Displayed text for the reference */
  referenceLabel: string;
  /** Should be the sub-path to the editor for the reference (e.g. '/screens/step_name', '/actions/wfv_123/step_token') */
  referenceSubPath: string;
};

export type FieldReferenceMap = Record<string, FieldReferenceConfig[]>;

type FieldStepPair = {
  field: string;
  /** Pretty display name of the step containing the field */
  stepDisplayName: string;
  /** Identifier for the step. Used to build the `referenceSubPath` of `FieldReferenceConfig` */
  stepSubPath: string;
};

export type UiFormFieldOptionComponentConfigPathPair = {
  component: UiFormFieldOptionComponentConfig;
  path: string;
};

export const buildFieldReferenceMap = ({
  fieldSchema,
  steps,
  workflowVersionMap,
  vtmplVersionMap,
}: {
  fieldSchema: FieldSchema[];
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
  vtmplVersionMap: VerificationTemplateVersionMap;
}): FieldReferenceMap => {
  const fieldReferenceMap: FieldReferenceMap = fieldSchema
    .filter(({ key }) => key != null)
    .reduce(
      (map, field) => ({
        ...map,
        [field.key]: [],
      }),
      {},
    );
  buildStepFieldReferences(fieldReferenceMap, steps);
  buildWorkflowFieldReferences(fieldReferenceMap, workflowVersionMap);
  buildVerificationTemplateFieldReferences(fieldReferenceMap, vtmplVersionMap);

  // Prune any duplicate references. Check for dupes on the `referenceSubPath`
  // since steps that share the same name may still be different steps.
  Object.entries(fieldReferenceMap).forEach(([key, references]) => {
    const dedupedReferences = references.filter(
      (currRef, index, self) =>
        index ===
        self.findIndex((selfRef) => selfRef.referenceSubPath === currRef.referenceSubPath),
    );
    fieldReferenceMap[key] = dedupedReferences;
  });

  return fieldReferenceMap;
};

/**
 * Method to derive all component configs and their paths that reference
 * a form field of Choices schema type
 */
export const getUiFormFieldOptionComponentConfigAndPathPairs = (
  fieldName: string,
  steps: InquiryTemplateVersionStep[],
): UiFormFieldOptionComponentConfigPathPair[] =>
  steps.filter(isUiStepConfig).flatMap((step) =>
    uiStepAllComponentConfigs(step)
      .filter(
        (component): component is UiFormFieldOptionComponentConfig =>
          isUiFormFieldOptionComponentConfig(component) && component.config.field === fieldName,
      )
      .flatMap((component) => {
        const stepIndex = steps.findIndex((currentStep) => currentStep.name === step.name);
        const componentPath = findComponentConfigPath(
          (componentConfig) => componentConfig.name === component.name,
          step.config.components,
        );
        return {
          path: `steps.${stepIndex}.config.components.${(componentPath ?? []).join('.')}`,
          component,
        };
      }),
  );

const buildStepFieldReferences = (
  fieldMap: FieldReferenceMap,
  steps: InquiryTemplateVersionStep[],
): void => {
  const fieldToSteps: FieldStepPair[] = steps.filter(isScreenStep).flatMap((step) => {
    const stepDisplayName = getDisplayNameForStep(step);
    switch (step.type) {
      case UiStepType.Document:
      case UiStepType.GovernmentId:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return CombinedStepFieldKeyMap[step.type].map((configKey: keyof CombinedStepConfig) => ({
          field: step.config[configKey] as string,
          stepDisplayName,
          stepSubPath: step.name,
        }));
      case UiStepType.Ui:
        return getFieldStepPairsReferencedByInputComponents(step, stepDisplayName).concat(
          getFieldStepPairsReferencedByJsonLogicComponents(step, stepDisplayName),
        );
      // NOTE: no editable fields on Integration step
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Loop:
        return [];
    }
  });
  fieldToSteps.forEach(({ field, stepDisplayName, stepSubPath }) => {
    if (fieldMap[field] != null) {
      const referenceLabel = stepDisplayName.toLowerCase().includes('step')
        ? stepDisplayName
        : `${stepDisplayName} step`;
      fieldMap[field].push({
        referenceLabel,
        referenceSubPath: `/screens/${stepSubPath}`,
      });
    }
  });
};

const getFieldStepPairsReferencedByInputComponents = (
  uiStep: InquiryTemplateVersionStepUi,
  stepDisplayName: string,
): FieldStepPair[] =>
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  uiStepAllComponentConfigs(uiStep)
    .filter(isUiFormComponentConfig)
    .flatMap((component) =>
      Object.entries(component.config)
        .map(([configKey, fieldKeyVal]) => {
          // return pairs when field attribute key found and field key value is populated
          if (configKey.includes('field') && fieldKeyVal != null) {
            const componentTypeDisplayName =
              ScreenEditorComponentConfigTypes[component.type].displayName;
            const searchParams = new URLSearchParams([
              [DISPLAY_FOCUS_SEARCH_PARAM, SelectedComponentTab.FIELD],
            ]);
            return {
              field: fieldKeyVal,
              stepDisplayName: `${componentTypeDisplayName} in ${stepDisplayName}`,
              stepSubPath: `${uiStep.name}/${component.name}?${searchParams.toString()}`,
            };
          }
        })
        .filter(Boolean),
    );

const extractFieldNamesRec = (fieldNameSet: Set<string>, jsonLogic: ComponentJsonLogicRule) => {
  Object.entries(jsonLogic).forEach(([key, value]) => {
    // if the key is var, we know that the value is a field value
    if (key === 'var') {
      fieldNameSet.add(
        (value as string)
          // remove quotes around field values string
          .replace(/['"]+/g, '')
          // remove `field_values.` suffix from string
          .replace(/field_values./g, ''),
      );
      // otherwise its another json logic operation, so recursively call extractFieldNamesRec
    } else if (Array.isArray(value)) {
      value.forEach((rule: ComponentJsonLogicRule) => {
        extractFieldNamesRec(fieldNameSet, rule);
      });
    }
  });
};

export const getFieldStepPairsReferencedByJsonLogicComponents = (
  uiStep: InquiryTemplateVersionStepUi,
  stepDisplayName: string,
): FieldStepPair[] => {
  const componentsWithJsonLogic = Array.from(
    getConfigOnComponentWithJSONLogic(uiStepAllComponentConfigs(uiStep)),
  );
  if (componentsWithJsonLogic.length === 0) return [];

  return componentsWithJsonLogic.flatMap(({ component }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const jsonLogicProperties = Object.entries(component.config)
      .filter(
        ([key, jsonLogic]) =>
          COMPONENT_JSONLOGIC_KEYS.filter((logicKey) =>
            logicKey.endsWith(SERVER_EVALUATED_SUFFIX),
          ).includes(key as ComponentJsonLogicKey) && isConfigJSONLogic(jsonLogic),
      )
      .map(
        ([_, jsonLogic]) =>
          // jsonLogic needs to be formatted so that we're not skipping over references in inverted rules
          formatJSONLogicRule(jsonLogic as ComponentJsonLogicRule).rules,
      );
    const fieldNames = new Set<string>();
    jsonLogicProperties.forEach((jsonLogic) => {
      if (jsonLogic) extractFieldNamesRec(fieldNames, jsonLogic);
    });
    const componentTypeDisplayName = ScreenEditorComponentConfigTypes[component.type].displayName;
    const searchParams = new URLSearchParams([
      [DISPLAY_FOCUS_SEARCH_PARAM, SelectedComponentTab.LOGIC],
    ]);
    return Array.from(fieldNames).map((fieldName) => ({
      field: fieldName,
      stepDisplayName: `${componentTypeDisplayName} logic in ${stepDisplayName}`,
      stepSubPath: `${uiStep.name}/${component.name}?${searchParams.toString()}`,
    }));
  });
};

const buildWorkflowFieldReferences = (
  fieldMap: FieldReferenceMap,
  workflowVersionMap: WorkflowVersionMap,
): void => {
  const fieldToSteps: FieldStepPair[] = Object.values(workflowVersionMap).flatMap(
    (workflowVersion: WorkflowVersion) =>
      getWorkflowStepFieldPairs(
        getAllWorkflowActionSteps(workflowVersion.attributes['steps-config'] ?? []),
        workflowVersion.id,
      ),
  );
  fieldToSteps.forEach(({ field, stepDisplayName, stepSubPath }) => {
    if (fieldMap[field] != null) {
      fieldMap[field].push({
        referenceLabel: `${stepDisplayName} workflow action`,
        referenceSubPath: `/actions/${stepSubPath}`,
      });
    }
  });
};

const getWorkflowStepFieldPairs = (
  steps: WorkflowStep[],
  workflowVersionId: string,
): FieldStepPair[] => {
  return steps.filter(isFieldsReferenceActionStep).flatMap((step) => {
    let fields: string[] = [];
    const stepName = step.name;
    switch (step.action.key) {
      case WorkflowStepActionKey.CreateVerificationAamvaFromInquiry:
      case WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry:
      case WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry:
      case WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry:
      case WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry:
      case WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry:
      case WorkflowStepActionKey.RunVerificationDatabaseBusiness:
      case WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry:
      case WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry:
      case WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry:
      case WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry:
      case WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry:
        fields = Object.values(step.action.params['verification-to-inquiry-field-mapping']);
        break;
      case WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry:
        fields = Object.values(step.action.params['verification-to-inquiry-field-mapping']).concat(
          step.action.params['field-key-kica-flow-url'],
        );
        break;
      case WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry:
        fields = Object.values(step.action.params['verification-to-inquiry-field-mapping']).concat(
          step.action.params['field-key-aadhaar-flow-url'],
        );
        break;
      case WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry:
        fields = [step.action.params['field-key-finnish-trust-network-flow-url']];
        break;
      case WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry:
        fields = [step.action.params['field-key-one-id-flow-url']];
        break;
      case WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry:
      case WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry:
      case WorkflowStepActionKey.CreateVerificationDocumentFromInquiry:
        fields = [step.action.params['field-key-document']];
        break;
      case WorkflowStepActionKey.CreateVerificationSelfieFromInquiry:
        fields = [step.action.params['field-key-selfie']];
        break;
      case WorkflowStepActionKey.UpdateAccountFieldsFromInquiry:
        fields = Object.values(step.action.params['account-to-inquiry-field-mapping']);
        break;
      case WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase:
      case WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId:
        fields = Object.keys(step.action.params['inquiry-to-verification-field-mapping']).map(
          (fieldKey) => snakeCase(fieldKey),
        );
        break;
      case WorkflowStepActionKey.UpdateObject: {
        const { fields: paramFields, values } = step.action.params;
        if (step.action.params.object === BaseTrigger) {
          // check snake-cased keys for fields AND values if target object is the inquiry
          fields = [...Object.keys(paramFields), ...Object.keys(values ?? {})].map((fieldKey) =>
            snakeCase(fieldKey),
          );
        } else {
          // only check field mapping values with 'trigger.fields.' prefix when the inquiry is read from.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          fields = Object.values(paramFields)
            .map((paramField) => {
              if (typeof paramField === 'string' && paramField.includes(INQUIRY_FIELD_PREFIX)) {
                return paramField.replace(INQUIRY_FIELD_PREFIX, '');
              }
              return null;
            })
            .filter(Boolean);
        }
        break;
      }
      default:
        break;
    }
    return fields.map((field) => ({
      field,
      stepSubPath: `${workflowVersionId}/${step.token}`,
      stepDisplayName: stepName,
    }));
  });
};

const buildVerificationTemplateFieldReferences = (
  fieldMap: FieldReferenceMap,
  vtmplVersionMap: VerificationTemplateVersionMap,
): void => {
  Object.values(vtmplVersionMap).forEach((vtmplVersion) => {
    switch (vtmplVersion.type) {
      case ApiModel.VerificationTemplateVersionDocument: {
        const extractions = (vtmplVersion.attributes?.extractions || []).filter(
          (extraction) => extraction?.['extraction-type'] === ExtractionType.Guided,
        );
        const fieldNames = extractions
          .flatMap((extraction) => {
            switch (extraction?.version) {
              case '2.0':
                switch (extraction['field-type']) {
                  case ExtractionFieldType.NameFull:
                    return EXTRACTION_FIELD_TYPE_NAME_FULL_FIELDS;
                  case ExtractionFieldType.Address:
                    return EXTRACTION_FIELD_TYPE_ADDRESS_FIELDS;
                  default:
                    return extraction['field-name'];
                }
              case '3.0':
                if (extraction?.subfields?.length === 0) {
                  return extraction['field-name'];
                }
                return extraction?.subfields?.map((subfield) => subfield?.['source-field']);
            }
          })
          .filter(Boolean) as string[];

        fieldNames.forEach((field) => {
          const displayName =
            vtmplVersion.attributes?.name ??
            vtmplVersion.attributes?.['template-name'] ??
            VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[
              ApiModel.VerificationTemplateVersionDocument
            ];
          if (fieldMap[field] != null) {
            fieldMap[field].push({
              referenceLabel: `${displayName} verification template extraction`,
              referenceSubPath: `/config/verifications/${vtmplVersion.id}`,
            });
          }
        });

        break;
      }
    }
  });
};
